import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Chip, TableSortLabel, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

interface Props {
  tableHeadings: { [key: string]: string };
}

const EditReceiptsReviewTable = ({ tableHeadings }: Props) => {
  const headCells = Object.entries(tableHeadings);
  const {
    manualEditReceiptsPayload,
    linkedFmes,
    allCurrencies,
    allCompanies,
    editUnpairPayload,
  } = useSelector((state: any) => state.receipts);
  const effectiveFmes = editUnpairPayload
    ? linkedFmes.filter(
        (fme: any) =>
          !editUnpairPayload.fme_shared_uids.includes(fme.fme_shared_uid)
      )
    : linkedFmes;

  const rows = React.useMemo(() => {
    if (!linkedFmes.length || !manualEditReceiptsPayload.length) return [];

    const receipt = manualEditReceiptsPayload[0];

    const companyMap = new Map(
      allCompanies.map((c: any) => [c.shared_uid, c.company_name])
    );

    const currencyMap = new Map(
      allCurrencies.map((c: any) => [c.shared_uid, c.currency_symbol])
    );

    return [
      {
        cron_id: receipt.receipt_id,
        company: companyMap.get(receipt.company_shared_uid) ?? "N/A",
        date: receipt.receipt_date,
        receipt_number: receipt.receipt_id,
        receipt_amount: receipt.receipt_amount,
        receipt_status: receipt.receipt_status,
        currency_symbol:
          currencyMap.get(receipt.receipt_currency_shared_uid) ?? "",
        fmes: effectiveFmes.map((fme: any) => fme.fme_id),
      },
    ];
    // eslint-disable-next-line
  }, [
    linkedFmes,
    manualEditReceiptsPayload,
    allCurrencies,
    allCompanies,
    editUnpairPayload,
  ]);

  return (
    <>
      <Typography>Preview:</Typography>
      <TableContainer
        className="receiptsTable EditReceiptsLinkedFmeTable"
        sx={{ mb: 3, mt: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map(([_, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel>
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => {
              return (
                <React.Fragment key={row.cron_id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "company" ? (
                          <>{row[key] ? row[key] : "N/A"} </>
                        ) : key === "date" ? (
                          <>
                            {row[key]
                              ? dayjs(row[key]).format("DD/MM/YYYY")
                              : "N/A"}
                          </>
                        ) : key === "receipt_number" ? (
                          <>{row[key] ? row[key] : "N/A"} </>
                        ) : key === "receipt_amount" ? (
                          <>
                            <Box display={"flex"} alignItems={"center"}>
                              {row[key] ? row[key] : "N/A"}{" "}
                              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                                {row.currency_symbol}
                              </span>
                            </Box>
                          </>
                        ) : key === "receipt_status" ? (
                          <>
                            <div className="AlertItems">
                              {row[key] ? row[key] : "N/A"}
                            </div>
                          </>
                        ) : key === "fme" ? (
                          <div className="chip-data">
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              {/* First FME only */}
                              {row.fmes.length > 0 && (
                                <Chip
                                  label={row.fmes[0]}
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    color: "rgba(0, 0, 0, 0.87)",
                                    borderRadius: "5px",
                                    boxShadow: "1px 1px 2px 1px #00000030",
                                    height: "26px",
                                    fontWeight: "500",
                                    border: "1px solid #ffffff",
                                  }}
                                />
                              )}

                              {/* Tooltip for remaining FMEs */}
                              {row.fmes.length > 1 && (
                                <Tooltip title={row.fmes.join(", ")} arrow>
                                  <Chip
                                    label={`+${row.fmes.length - 1}`}
                                    sx={{
                                      backgroundColor: "#f0f0f0",
                                      cursor: "pointer",
                                      height: "26px",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default EditReceiptsReviewTable;
